import {
  QuestionCircleOutlined
} from "@ant-design/icons";
import {
  Button,
  Card,
  Form,
  Input,
  message,
  PageHeader,
  Select,
  Tabs,
  Tooltip,
  Typography
} from "antd";
import { useForm } from "antd/lib/form/Form";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionContext } from "../../App";
import {
  ChangePasswordRequest,
  UpdateCompanyRequest,
  UpdateCompanyUserRequest,
} from "../../constants/apiRequestResponse";
import { CompanySizes, Gender } from "../../constants/types";
import {
  changeCompanyUserPassword,
  updateCompany,
  updateCompanyUser
} from "../../services/api";
import { EditProfilePageKeys } from "../../services/i8tn/EditProfilePage/keys";
import { getQueryParam, setQueryParam } from "../../util";

type CompanyFormFields = {
  _id: string

  // i.e. the File for the company logo
  file?: File,
  logoURL?: string,
  emailDomains?: string[],
  name?: string,
  industry?: string,
  location?: string,
  size?: CompanySizes | "",
}

type CompanyUserFormFields = {
  name?: string,
  email?: string,
  gender?: Gender,
  mobile?: string
}

export default function EditProfilePage() {
  const context = useContext(SessionContext);
  const { t } = useTranslation();
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [base64, setBase64] = useState('')

  const [companyForm] = useForm<CompanyFormFields>()
  const [companyUserForm] = useForm<CompanyUserFormFields>()

  useEffect(() => {
    if(!context?.company) return
    const fieldsValue: CompanyFormFields = {
      _id: context.company._id,
      logoURL: context.company.logo,
      emailDomains: context.company.emailDomains,
      name: context.company.name,
      industry: context.company.industry,
      location: context.company.location,
      size: context.company.size,
    }
    companyForm.setFieldsValue(fieldsValue)
  }, [context?.company, companyForm])

  useEffect(() => {
    if(!context?.companyUser) return
    const fieldsValue: CompanyUserFormFields = {
      name: context.companyUser.name,
      gender: context.companyUser.gender,
      email: context.companyUser.email,
      mobile: context.companyUser.mobile,
    }
    companyUserForm.setFieldsValue(fieldsValue)
  }, [context?.companyUser, companyUserForm])

  return (
    <div className="page-content">
      <PageHeader
        className="site-page-header"
        title={t(EditProfilePageKeys.editProfilePage_editProfileTitle)}
      />
      <Card
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Tabs
          tabPosition="left"
          defaultActiveKey={getQueryParam("key") || "User"}
          onChange={(key) => {
            setQueryParam("key", key);
          }}
        >
          <Tabs.TabPane
            tab={t(EditProfilePageKeys.editProfilePage_userTab)}
            key="User"
          >
            <Typography.Title level={3}>
              {t(EditProfilePageKeys.editProfilePage_userProfileTabTitle)}
            </Typography.Title>
            <Form
              layout="vertical"
              onFinish={(v) => {
                setIsSaveLoading(true);

                const req: UpdateCompanyUserRequest = {
                  companyUser: v
                }
                updateCompanyUser(req)
                  .then(() => {
                    message.success("Profile updated");
                  })
                  .catch((err) => {
                    message.error(err?.toString());
                  })
                  .finally(() => setIsSaveLoading(false));
              }}
              form={companyUserForm}
              style={{
                width: 500,
              }}
            >
              <Form.Item hidden name="_id" />

              <Form.Item
                label={t(
                  EditProfilePageKeys.editProfilePage_userProfileNameString
                )}
                name="name"
                rules={[{ required: true }]}
              >
                <Input placeholder="John" />
              </Form.Item>
              <Form.Item
                label={t(
                  EditProfilePageKeys.editProfilePage_userProfileEmailString
                )}
                name="email"
                rules={[
                  /**
                   * 1/6/2022 daniel.kwok
                   * Not sure if company emailDomain validation is required here.
                   * Might need to cross check
                   */
                  {
                    required: true,
                    type: "email",
                    validator: (_, value) => {
                      for (let emailDomain of context?.company?.emailDomains ||
                        []) {
                        const regex = new RegExp(`.+@${emailDomain}$`);
                        if (value.match(regex)) {
                          return Promise.resolve();
                        }
                      }

                      return Promise.reject();
                    },
                    message: `Email must end with one of ${context?.company?.emailDomains.toString()}. E.g. johndoe@${
                      context?.company?.emailDomains[0]
                    }`,
                  },
                ]}
              >
                <Input placeholder="johndoe@email.com" />
              </Form.Item>
              <Form.Item
                label={t(
                  EditProfilePageKeys.editProfilePage_userProfileGenderString
                )}
                name="gender"
                rules={[
                  {
                    required: true,
                    type: "enum",
                    enum: [Gender.Male, Gender.Female, Gender.Other],
                  },
                ]}
              >
                <Select showSearch placeholder={Gender.Male}>
                  {[Gender.Male, Gender.Female, Gender.Other].map((gender) => {
                    return (
                      <Select.Option key={gender} value={gender}>
                        {gender}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>

              <Form.Item
                label={t(
                  EditProfilePageKeys.editProfilePage_userProfileMobileString
                )}
                name="mobile"
                rules={[{ required: true }]}
              >
                <Input placeholder="0123456789" />
              </Form.Item>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 5,
                }}
              >
                <Button loading={isSaveLoading} htmlType="submit" type="primary">
                  <>
                    {t(EditProfilePageKeys.editProfilePage_userProfileSaveBtn)}
                  </>
                </Button>
              </div>
            </Form>
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={t(EditProfilePageKeys.editProfilePage_companyTab)}
            key="Company"
          >
            <Typography.Title level={3}>
              {t(EditProfilePageKeys.editProfilePage_companyTabTitle)}
            </Typography.Title>
            
            <Form
              layout="vertical"
              onFinish={async (formValues) => {
                setIsSaveLoading(true)
                const companyId = formValues._id

                const updateCompanyRequest: UpdateCompanyRequest = formValues
                try{
                  await updateCompany(updateCompanyRequest, companyId)
                  message.success(
                    t(
                      EditProfilePageKeys.editProfilePage_companySaveCompanySuccessMessage
                    )
                  )
                }catch(err){
                  message.error(err?.toString())
                }

                setIsSaveLoading(false)
              }}
              style={{
                width: 500,
              }}
              form={companyForm}
            >
              <Form.Item hidden name="_id" />
              <img alt="" src={base64 || companyForm.getFieldValue('logoURL')} width={300} />

              <Form.Item>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if(!file) {
                      throw new Error(`Error attaching file`)
                    }
                    
                    companyForm.setFieldsValue({
                      file: file,
                    })

                    // convert to base64 & set state for viewing purposes only
                    var reader = new FileReader();
                    if (file) {
                      reader.readAsDataURL(file);

                      reader.onload = async function () {
                        const _base64 = reader.result as string
                        if (!_base64) return

                        try{
                          setBase64(_base64)
                        }catch(err) {
                          message.error(err?.toString())
                        }
                          
                      };
                      reader.onerror = function (error) {
                        console.log("Error: ", error);
                      };
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    {t(
                      EditProfilePageKeys.editProfilePage_companyEmailDomainString
                    )}{" "}
                    &nbsp;
                    <Tooltip
                      title={t(
                        EditProfilePageKeys.editProfilePage_companyEmailDomainToolTip
                      )}
                    >
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                }
                name="emailDomains"
                required
                help="E.g. gmail.com, acme.com, thedevelopmentcentre.com"
              >
                <Select mode="tags" style={{ width: "100%" }}></Select>
              </Form.Item>

              <Form.Item
                label={t(EditProfilePageKeys.editProfilePage_companyNameString)}
                name="name"
                rules={[{ required: true }]}
              >
                <Input
                  placeholder={t(
                    EditProfilePageKeys.editProfilePage_companyNamePlaceholder
                  )}
                />
              </Form.Item>
              <Form.Item
                label={t(
                  EditProfilePageKeys.editProfilePage_companyIndustryString
                )}
                name="industry"
              >
                <Input
                  placeholder={t(
                    EditProfilePageKeys.editProfilePage_companyIndustryPlaceholder
                  )}
                />
              </Form.Item>
              <Form.Item
                label={t(
                  EditProfilePageKeys.editProfilePage_companyLocationString
                )}
                name="location"
              >
                <Input
                  placeholder={t(
                    EditProfilePageKeys.editProfilePage_companyLocationPlaceholder
                  )}
                />
              </Form.Item>
              <Form.Item
                label={t(EditProfilePageKeys.editProfilePage_companySizeString)}
                name="size"
              >
                <Select
                  onChange={v => {
                    /**
                     * if we clear this option, the value will be undefined
                     * this leads to us not being able to distinguish whether we want to
                     * set value to empty
                     * or
                     * do not update this field
                     * when we call the PUT api
                     * 
                     * as such extra handling is required here 
                     */
                    if(v === undefined){
                      companyForm.setFieldsValue({
                        size: CompanySizes.empty_value
                      })
                    }
                  }}
                  showSearch
                  allowClear
                  placeholder={CompanySizes.medium}>
                  {[
                    CompanySizes.enterprise,
                    CompanySizes.large,
                    CompanySizes.medium,
                    CompanySizes.small,
                    CompanySizes.micro,
                  ].map((size) => {
                    return (
                      <Select.Option key={size} value={size}>
                        {size}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 5,
                }}
              >
                <Button loading={isSaveLoading} htmlType="submit" type="primary">
                  {t(EditProfilePageKeys.editProfilePage_companySaveBtn)}
                </Button>
              </div>
            </Form>
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={t(EditProfilePageKeys.editProfilePage_resetPasswordTab)}
            key="Reset password"
          >
            <Typography.Title level={3}>
              {t(EditProfilePageKeys.editProfilePage_resetPasswordTabTitle)}
            </Typography.Title>
            <Form
              layout="vertical"
              onFinish={async (v: {
                oldPassword: string;
                newPassword: string;
                confirmNewPassword: string;
              }) => {
                try {
                  setIsSaveLoading(true);
                  if (v.newPassword !== v.confirmNewPassword)
                    throw new Error(`New passwords don't match.`);

                  const req: ChangePasswordRequest = {
                    oldPassword: v.oldPassword,
                    newPassword: v.newPassword,
                  };

                  const res = await changeCompanyUserPassword(req);
                  if (!res.success) throw new Error(res.message);

                  message.success(`Password reset successful`);

                  setIsSaveLoading(false);
                } catch (err) {
                  message.error(
                    typeof err === "string"
                      ? err
                      : typeof err === "object"
                      ? err?.toString()
                      : `Something went wrong`
                  );
                  setIsSaveLoading(false);
                }
              }}
              style={{
                width: 500,
              }}
            >
              <Form.Item
                label={t(
                  EditProfilePageKeys.editProfilePage_resetPasswordOldString
                )}
                name="oldPassword"
                rules={[{ required: true }]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                label={t(
                  EditProfilePageKeys.editProfilePage_resetPasswordNewString
                )}
                name="newPassword"
                rules={[{ required: true }]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                label={t(
                  EditProfilePageKeys.editProfilePage_resetPasswordConfirmString
                )}
                name="confirmNewPassword"
                rules={[{ required: true }]}
              >
                <Input.Password />
              </Form.Item>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 5,
                }}
              >
                <Button htmlType="submit" type="primary" loading={isSaveLoading}>
                  {t(EditProfilePageKeys.editProfilePage_resetPasswordResetBtn)}
                </Button>
              </div>
            </Form>
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
}
